import React, { useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { Row, Col, Layout, Button, Modal, Form, Input, Skeleton, message, Dropdown, Space } from 'antd';
import { PlusOutlined, EditOutlined, MoreOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useBranches } from '../../hooks/useBranches';
import { useAllPlaces } from '../../hooks/usePlaces';
import { useDispatch, useSelector } from 'react-redux';
import { addBranch, updateBranch, setSelectedBranch, setSelectedPlace } from '../../store/slices/branchesSlice';
import { JWTContext } from '../../contexts/jwtContext';
import ScopeCard from './ScopeCard';
import './styles.css';

const { Content } = Layout;

// İl/ilçesi girilmemiş şubeler kaybolmasın diye kendi kovalarında toplanıyor.
const UNSET = 'Belirtilmemiş';
const cityOf = (place) => place.city || UNSET;
const districtOf = (place) => place.district || UNSET;

// Belirtilmemiş kovası her zaman en sona; gerisi Türkçe alfabetik.
const byName = (a, b) => {
    if (a.name === UNSET) return 1;
    if (b.name === UNSET) return -1;
    return a.name.localeCompare(b.name, 'tr');
};

const groupPlaces = (places, keyOf) => {
    const map = new Map();
    places.forEach((place) => {
        const name = keyOf(place);
        const entry = map.get(name) || { name, placeCount: 0, studentCount: 0 };
        entry.placeCount += 1;
        entry.studentCount += place.studentCount || 0;
        map.set(name, entry);
    });
    return [...map.values()].sort(byName);
};

const Stat = ({ value, unit }) => (
    <span className="scope-card__stat">
        <span className={`scope-card__num${value ? '' : ' scope-card__num--muted'}`}>{value ?? 0}</span>
        <span className="scope-card__unit">{unit}</span>
    </span>
);

const ScopeSelection = () => {
    const { branches, loading, refetchBranches } = useBranches();
    const { places: allPlaces, loading: placesLoading } = useAllPlaces();
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { logout } = useContext(JWTContext);

    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);

    // İl ve ilçe yalnızca seçimi daraltan huni; kapsam yine branş + şube.
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);

    const districtStepRef = useRef(null);
    const branchStepRef = useRef(null);
    const placeStepRef = useRef(null);

    // Hiç şube yoksa il/ilçe kademesi boş kalır; branş adımı yine de açılmalı ki
    // kullanıcı branş seçip /locations'tan ilk şubesini ekleyebilsin.
    const noPlacesAtAll = !placesLoading && allPlaces.length === 0;

    const cities = useMemo(() => groupPlaces(allPlaces, cityOf), [allPlaces]);

    const districts = useMemo(() => {
        if (!selectedCity) return [];
        return groupPlaces(allPlaces.filter((p) => cityOf(p) === selectedCity), districtOf);
    }, [allPlaces, selectedCity]);

    // Seçili ilçedeki şubeler; branş ve şube adımlarının kaynağı.
    const placesInDistrict = useMemo(() => {
        if (!selectedCity || !selectedDistrict) return [];
        return allPlaces.filter((p) => cityOf(p) === selectedCity && districtOf(p) === selectedDistrict);
    }, [allPlaces, selectedCity, selectedDistrict]);

    const branchOptions = useMemo(() => {
        if (noPlacesAtAll) {
            return branches.map((branch) => ({ branch, placeCount: 0, studentCount: 0, placeless: true }));
        }
        if (!selectedDistrict) return [];

        const withPlaces = branches
            .filter((branch) => placesInDistrict.some((p) => p.branchId === branch.id))
            .map((branch) => {
                const owned = placesInDistrict.filter((p) => p.branchId === branch.id);
                return {
                    branch,
                    placeCount: owned.length,
                    studentCount: owned.reduce((sum, p) => sum + (p.studentCount || 0), 0),
                    placeless: false
                };
            });

        // Hiç şubesi olmayan branşlar da listelensin, yoksa yeni açılan branşa
        // şube eklemenin yolu kalmıyor.
        const placeless = branches
            .filter((branch) => !allPlaces.some((p) => p.branchId === branch.id))
            .map((branch) => ({ branch, placeCount: 0, studentCount: 0, placeless: true }));

        return [...withPlaces, ...placeless];
    }, [branches, placesInDistrict, allPlaces, selectedDistrict, noPlacesAtAll]);

    const placeOptions = useMemo(() => {
        if (!selectedBranch) return [];
        return placesInDistrict.filter((p) => p.branchId === selectedBranch.id);
    }, [placesInDistrict, selectedBranch]);

    // Dar ekranda sonraki adım katlamanın altında kalıyor; seçim yapılınca oraya kaydır.
    const scrollToStep = useCallback((ref) => {
        if (window.innerWidth < 768 && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    useEffect(() => {
        if (selectedBranch) scrollToStep(placeStepRef);
    }, [selectedBranch, scrollToStep]);

    // Ekrana dönüldüğünde huni boş başlamasın: önceki şube varsa onun il/ilçesine
    // konumlan, yoksa yarım kalmış branş seçimini bırak ki 04. adım yanlış boş görünmesin.
    const bootstrapped = useRef(false);
    useEffect(() => {
        if (bootstrapped.current) return;
        bootstrapped.current = true;

        if (selectedPlace) {
            setSelectedCity(cityOf(selectedPlace));
            setSelectedDistrict(districtOf(selectedPlace));
        } else if (selectedBranch) {
            dispatch(setSelectedBranch(null));
        }
    }, [selectedPlace, selectedBranch, dispatch]);

    const handleCitySelect = (name) => {
        setSelectedCity(name);
        setSelectedDistrict(null);
        dispatch(setSelectedBranch(null));
        scrollToStep(districtStepRef);
    };

    const handleDistrictSelect = (name) => {
        setSelectedDistrict(name);
        dispatch(setSelectedBranch(null));
        scrollToStep(branchStepRef);
    };

    const handleAddCancel = () => {
        addForm.resetFields();
        setIsAddModalVisible(false);
    };

    const handleEditCancel = () => {
        editForm.resetFields();
        setEditingBranch(null);
        setIsEditModalVisible(false);
    };

    const handleAddSubmit = async (values) => {
        try {
            await dispatch(addBranch({ name: values.name })).unwrap();
            message.success('Branş eklendi.');
            refetchBranches();
            handleAddCancel();
        } catch (error) {
            console.error('Branch add error details:', error);
            message.error(`Branş eklenemedi: ${error.message || error}`);
        }
    };

    const handleEditSubmit = async (values) => {
        try {
            await dispatch(updateBranch({ id: editingBranch.id, name: values.name })).unwrap();
            message.success('Branş güncellendi.');
            refetchBranches();
            handleEditCancel();
        } catch (error) {
            console.error('Branch update error:', error);
            message.error('Branş güncellenemedi.');
        }
    };

    const handleLogout = () => {
        logout();
        localStorage.clear();
        navigate('/login');
    };

    const branchMenu = (branch) => [
        {
            key: 'edit',
            label: 'Adını değiştir',
            icon: <EditOutlined />,
            onClick: () => {
                setEditingBranch(branch);
                editForm.setFieldsValue({ name: branch.name });
                setIsEditModalVisible(true);
            }
        }
    ];

    const skeletons = (count) => (
        <Row gutter={[12, 12]}>
            {Array.from({ length: count }).map((_, i) => (
                <Col xs={24} sm={12} md={8} key={i}>
                    {/* Kart ölçüsünde iskelet: içerik gelince zıplama olmuyor. */}
                    <div className="scope-card" style={{ cursor: 'default' }}>
                        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '70%' }} />
                    </div>
                </Col>
            ))}
        </Row>
    );

    const sectionHead = (step, name, of, action) => (
        <div className="scope-section__head">
            <span className="scope-section__label">
                <span className="scope-section__step">{step}</span>
                <span className="scope-section__name">{name}</span>
                {of && <span className="scope-section__of">— {of}</span>}
            </span>
            <span className="scope-section__rule" aria-hidden="true" />
            {action}
        </div>
    );

    return (
        <Layout className="scope-layout">
            <Content className="scope-content">
                <div className="scope-container">
                    <header className="scope-masthead">
                        <img src={require('../../assets/logo.png')} alt="Spor Alemi SK" className="scope-masthead__crest" />
                        <h1 className="scope-masthead__title">ÇALIŞMA ALANINIZI SEÇİN</h1>
                        <p className="scope-masthead__sub">İl ve ilçeyi seçin, ardından branşı ve çalışacağınız şubeyi.</p>
                    </header>

                    <div className="scope-board">
                        {/* 1 — İl */}
                        <section className="scope-section" aria-labelledby="scope-city">
                            {sectionHead('01', <span id="scope-city">İL</span>, null, null)}

                            {placesLoading && allPlaces.length === 0 ? skeletons(3) : cities.length === 0 ? (
                                <div className="scope-empty">
                                    <span>Henüz şube yok. Aşağıdan bir branş seçip ilk şubenizi ekleyin.</span>
                                </div>
                            ) : (
                                <Row gutter={[12, 12]}>
                                    {cities.map((city) => (
                                        <Col xs={24} sm={12} md={8} key={city.name}>
                                            <ScopeCard
                                                label={`${city.name} ilini seç`}
                                                selected={selectedCity === city.name}
                                                onSelect={() => handleCitySelect(city.name)}
                                            >
                                                <p className="scope-card__name">{city.name}</p>
                                                <div className="scope-card__stats">
                                                    <Stat value={city.placeCount} unit="ŞUBE" />
                                                    <Stat value={city.studentCount} unit="ÖĞRENCİ" />
                                                </div>
                                            </ScopeCard>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </section>

                        {/* 2 — İlçe */}
                        <section
                            ref={districtStepRef}
                            className={`scope-section${selectedCity ? '' : ' scope-section--locked'}`}
                            aria-labelledby="scope-district"
                        >
                            {sectionHead('02', <span id="scope-district">İLÇE</span>, selectedCity, null)}

                            {!selectedCity ? (
                                <div className="scope-empty">
                                    <span>Yukarıdan bir il seçin.</span>
                                </div>
                            ) : (
                                <Row gutter={[12, 12]}>
                                    {districts.map((district) => (
                                        <Col xs={24} sm={12} md={8} key={district.name}>
                                            <ScopeCard
                                                label={`${district.name} ilçesini seç`}
                                                selected={selectedDistrict === district.name}
                                                onSelect={() => handleDistrictSelect(district.name)}
                                            >
                                                <p className="scope-card__name">{district.name}</p>
                                                <div className="scope-card__stats">
                                                    <Stat value={district.placeCount} unit="ŞUBE" />
                                                    <Stat value={district.studentCount} unit="ÖĞRENCİ" />
                                                </div>
                                            </ScopeCard>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </section>

                        {/* 3 — Branş */}
                        <section
                            ref={branchStepRef}
                            className={`scope-section${selectedDistrict || noPlacesAtAll ? '' : ' scope-section--locked'}`}
                            aria-labelledby="scope-branch"
                        >
                            {sectionHead(
                                '03',
                                <span id="scope-branch">BRANŞ</span>,
                                selectedDistrict,
                                <Button size="small" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                                    Yeni branş
                                </Button>
                            )}

                            {!selectedDistrict && !noPlacesAtAll ? (
                                <div className="scope-empty">
                                    <span>Yukarıdan bir ilçe seçin.</span>
                                </div>
                            ) : loading && branches.length === 0 ? skeletons(3) : branchOptions.length === 0 ? (
                                <div className="scope-empty">
                                    <span>Henüz branş yok.</span>
                                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                                        İlk branşı ekle
                                    </Button>
                                </div>
                            ) : (
                                <Row gutter={[12, 12]}>
                                    {branchOptions.map(({ branch, placeCount, studentCount, placeless }) => (
                                        <Col xs={24} sm={12} md={8} key={branch.id}>
                                            <ScopeCard
                                                label={`${branch.name} branşını seç`}
                                                selected={selectedBranch?.id === branch.id}
                                                onSelect={() => dispatch(setSelectedBranch(branch))}
                                                menu={(
                                                    <Dropdown menu={{ items: branchMenu(branch) }} trigger={['click']}>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<MoreOutlined />}
                                                            aria-label={`${branch.name} branşı işlemleri`}
                                                        />
                                                    </Dropdown>
                                                )}
                                            >
                                                <p className="scope-card__name">{branch.name}</p>
                                                {placeless ? (
                                                    <p className="scope-card__address">Şubesi yok — seçip şube ekleyin</p>
                                                ) : (
                                                    <div className="scope-card__stats">
                                                        <Stat value={placeCount} unit="ŞUBE" />
                                                        <Stat value={studentCount} unit="ÖĞRENCİ" />
                                                    </div>
                                                )}
                                            </ScopeCard>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </section>

                        {/* 4 — Şube */}
                        <section
                            ref={placeStepRef}
                            className={`scope-section${selectedBranch ? '' : ' scope-section--locked'}`}
                            aria-labelledby="scope-place"
                        >
                            {sectionHead(
                                '04',
                                <span id="scope-place">ŞUBE</span>,
                                selectedBranch?.name,
                                selectedBranch && (
                                    <Button size="small" icon={<EnvironmentOutlined />} onClick={() => navigate('/locations')}>
                                        Şubeleri yönet
                                    </Button>
                                )
                            )}

                            {!selectedBranch ? (
                                <div className="scope-empty">
                                    <span>Yukarıdan bir branş seçin.</span>
                                </div>
                            ) : placeOptions.length === 0 ? (
                                <div className="scope-empty">
                                    <span>
                                        {selectedDistrict
                                            ? `${selectedBranch.name} branşının ${selectedDistrict} ilçesinde şubesi yok.`
                                            : `${selectedBranch.name} branşında şube yok.`}
                                    </span>
                                    <Button type="primary" size="small" icon={<EnvironmentOutlined />} onClick={() => navigate('/locations')}>
                                        Şube ekle
                                    </Button>
                                </div>
                            ) : (
                                <Row gutter={[12, 12]}>
                                    {placeOptions.map((place) => (
                                        <Col xs={24} sm={12} md={8} key={place.id}>
                                            <ScopeCard
                                                label={`${place.name} şubesiyle çalış`}
                                                onSelect={() => {
                                                    dispatch(setSelectedPlace(place));
                                                    navigate('/dashboard');
                                                }}
                                            >
                                                <p className="scope-card__name">{place.name}</p>
                                                {place.address && <p className="scope-card__address">{place.address}</p>}
                                                <div className="scope-card__stats">
                                                    <Stat value={place.groupCount} unit="GRUP" />
                                                    <Stat value={place.studentCount} unit="ÖĞRENCİ" />
                                                </div>
                                            </ScopeCard>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </section>
                    </div>

                    <div className="scope-foot">
                        <Button type="link" size="small" onClick={handleLogout}>Çıkış yap</Button>
                    </div>

                    <Modal title="Yeni branş" open={isAddModalVisible} onCancel={handleAddCancel} footer={null}>
                        <Form form={addForm} layout="vertical" onFinish={handleAddSubmit}>
                            <Form.Item
                                name="name"
                                label="Branş adı"
                                rules={[{ required: true, message: 'Branş adı gerekli.' }]}
                            >
                                <Input placeholder="Örn. Voleybol" />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Button type="primary" htmlType="submit" loading={loading}>Ekle</Button>
                                    <Button onClick={handleAddCancel}>Vazgeç</Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Modal>

                    <Modal title="Branş adını değiştir" open={isEditModalVisible} onCancel={handleEditCancel} footer={null}>
                        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
                            <Form.Item
                                name="name"
                                label="Branş adı"
                                rules={[{ required: true, message: 'Branş adı gerekli.' }]}
                            >
                                <Input placeholder="Örn. Voleybol" />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Button type="primary" htmlType="submit" loading={loading}>Kaydet</Button>
                                    <Button onClick={handleEditCancel}>Vazgeç</Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>
            </Content>
        </Layout>
    );
};

export default ScopeSelection;
